import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRightLong,
  faCircleCheck,
  faCircleExclamation,
  faShieldHalved,
  faDatabase,
  faLock,
  faSpinner,
  faClipboardCheck,
  faArrowRightArrowLeft,
  faCircleDot,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import { FinalizeMigrationPanel } from './components/FinalizeMigrationPanel';
import { ExecuteMigrationPanel } from './components/ExecuteMigrationPanel';
import { OwnershipTransferPanel } from './components/OwnershipTransferPanel';
import { deriveMigrationWorkflow, WORKFLOW_STEPS } from './migration-workflow';
import { useMigrationLive } from './useMigrationLive';
import { LiveProgressPanel } from './components/LiveProgressPanel';
import {
  type ProviderMigrationFinalizeResultDto,
  type ProviderMigrationOwnershipResultDto,
  type ProviderMigrationRunResultDto,
} from './api/provider-migrations.api';

/**
 * Provider migration — the STATE-DRIVEN detail and control view.
 *
 * ── WHAT DECIDES WHAT IS SHOWN ─────────────────────────────────────────────
 * Every action on this page is keyed on `deriveMigrationWorkflow(...)`, never on
 * `migration.status` directly. That distinction is the whole point: `VERIFYING`
 * covers BOTH "verified, ownership still on the source" and "verified, ownership
 * already moved", and those two states need opposite next actions. Keying the
 * Finalize panel on the status alone offered finalization in the state where the
 * server's `ASSET_NOT_ON_TARGET_PROVIDER` gate is guaranteed to refuse it, so the
 * operator's only way to discover the missing ownership step was to trigger a
 * 409 that said nothing they could act on.
 *
 * The gates were correct and are untouched. What changed is that this page now
 * asks the server where the assets are actually owned, and offers the ownership
 * transition — the step that was missing from the console entirely — instead of
 * a finalization that cannot succeed.
 *
 * ── WHAT IT STILL WILL NOT DO ──────────────────────────────────────────────
 * No cleanup, source-deletion, purge, provider-deactivation or ownership-
 * rollback control exists in this file or in the API client it imports. Every
 * mutating action it does offer — approve, run, ownership flip, finalize — is
 * behind its own explicit human confirmation, and none of them fires on mount,
 * render, reload or navigation.
 *
 * Every number shown is derived from the backend payload. Nothing about the
 * migration — its id, counts, byte total, status, progress or provider names —
 * is hardcoded here, and no provider or direction is special-cased anywhere.
 */

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtExactBytes(bytes: number | null | undefined): string {
  return bytes === null || bytes === undefined ? '—' : `${bytes.toLocaleString()} bytes`;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** A checksum is long and mostly noise on screen; show enough to compare by eye. */
function shortHash(hash: string | null): string {
  return hash ? `${hash.slice(0, 10)}…` : '—';
}

// ── Status presentation ───────────────────────────────────────────────────────

/**
 * Statuses that mean "planned, not yet executed". A migration in one of these
 * has had its scope calculated and NOTHING copied, so it must never be rendered
 * through the execution progress bar — 0/48 at 0% would read as a stalled or
 * failed copy rather than as a dry run that has not begun.
 */
const PRE_EXECUTION_STATUSES = new Set(['PENDING', 'DRY_RUN', 'DRY_RUN_COMPLETE', 'READY']);

const STATUS_TONE: Record<string, string> = {
  PENDING: 'bg-muted text-muted-foreground border-border',
  DRY_RUN: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
  DRY_RUN_COMPLETE: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
  COMPLETED: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  VERIFYING: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
  RUNNING: 'bg-primary/10 text-primary border-primary/30',
  READY: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
  PAUSED: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  FAILED: 'bg-destructive/10 text-destructive border-destructive/30',
  ROLLED_BACK: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  // Neither success nor failure. The destination copy succeeded; the source is
  // gone. A green tick would overstate it and a red cross would erase a copy
  // that demonstrably worked, so this reads as a recorded disposition.
  SOURCE_IRRECOVERABLE: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
        STATUS_TONE[status] ?? 'bg-muted text-muted-foreground border-border',
      )}
    >
      {status === 'COMPLETED' && <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />}
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ── Progress ──────────────────────────────────────────────────────────────────

/**
 * What a planned migration shows instead of a progress bar. It states plainly
 * that the scope exists and the objects do not, so nobody reads a fresh plan as
 * a migration that started and got nowhere.
 */
function DryRunPanel({ total, status }: { total: number; status: string }) {
  return (
    <div
      className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-5"
      data-testid="dry-run-panel"
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <FontAwesomeIcon icon={faClipboardCheck} className="h-4 w-4 text-sky-600" />
        Dry run — nothing copied yet
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        The migration scope has been calculated:{' '}
        <strong className="text-foreground">{total.toLocaleString()}</strong> object
        {total === 1 ? '' : 's'} would move.{' '}
        <strong className="text-foreground">No object has been copied</strong>,{' '}
        <strong className="text-foreground">no ownership has changed</strong>, and nothing has been
        deleted.
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        This plan is awaiting <strong className="text-foreground">explicit approval</strong>.
        Nothing is copied until an authorized operator confirms it.
      </p>
      <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">
        Current state:{' '}
        <span className="font-semibold text-foreground">{status.replace(/_/g, ' ')}</span>
      </p>
    </div>
  );
}

/**
 * The workflow, drawn. Its purpose is that an operator can see WHERE the
 * migration is and what comes next without knowing anything about the backend's
 * statuses or gates.
 *
 * `current` is the index of the phase the migration is in; earlier phases render
 * as done. It is derived, never hardcoded — a migration that never reaches a
 * phase never shows it as done.
 */
function WorkflowStepper({
  current,
  stage,
  complete,
}: {
  current: number;
  stage: string;
  /** True when the migration finished successfully — the terminal happy state. */
  complete: boolean;
}) {
  return (
    <ol
      className="flex flex-wrap items-center gap-x-1 gap-y-2"
      data-testid="workflow-stepper"
      aria-label="Migration workflow"
    >
      {WORKFLOW_STEPS.map((step, i) => {
        const isLast = i === WORKFLOW_STEPS.length - 1;
        const done = i < current;
        const active = i === current;
        // The terminal step is a SUCCESSFUL OUTCOME, not an unavailable action.
        // Rendering it in the same muted grey as a not-yet-reached step made a
        // finished migration look like it had stalled one step short.
        const finished = complete && isLast;

        return (
          <li key={step} className="flex items-center gap-1">
            <span
              data-testid={`workflow-step-${step.toLowerCase()}`}
              data-state={finished ? 'complete' : active ? 'current' : done ? 'done' : 'pending'}
              aria-current={active ? 'step' : undefined}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
                // Done: quietly, unmistakably finished. Filled, not outlined, so
                // it never reads as a control that could still be pressed.
                done &&
                  'border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
                // Current: the one place the eye should land.
                active &&
                  !finished &&
                  'border-primary/40 bg-primary/10 text-primary ring-2 ring-primary/20',
                // Terminal success: the strongest treatment on the row.
                finished &&
                  'border-transparent bg-emerald-600 text-white shadow-sm dark:bg-emerald-500',
                // Pending: clearly not yet reached, and clearly not disabled — it
                // is an outline, not a greyed-out button.
                !active &&
                  !done &&
                  !finished &&
                  'border-dashed border-border text-muted-foreground',
              )}
            >
              {(done || finished) && <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />}
              {active && !finished && <FontAwesomeIcon icon={faCircleDot} className="h-3 w-3" />}
              {step}
            </span>
            {!isLast && (
              <FontAwesomeIcon
                icon={faArrowRightLong}
                className={cn(
                  'h-3 w-3',
                  i < current ? 'text-emerald-500/60' : 'text-muted-foreground/40',
                )}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
      <li className="sr-only" data-testid="workflow-stage">
        {stage}
      </li>
    </ol>
  );
}

// ── Small building blocks ─────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  tone = 'default',
  hint,
}: {
  label: string;
  value: string | number;
  tone?: 'default' | 'good' | 'warn' | 'muted';
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-600',
          tone === 'warn' && 'text-amber-600',
          tone === 'muted' && 'text-muted-foreground',
          tone === 'default' && 'text-foreground',
        )}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b border-border py-2.5 last:border-0 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-56 shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-foreground">{children}</dd>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ProviderMigrationDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  // The page no longer loads once and hopes. `useMigrationLive` re-reads the
  // server while — and only while — the migration is actually moving, so the
  // workflow advances by itself and the operator never has to refresh to find
  // out what the backend did. Every figure below still comes from the server.
  const {
    migration,
    items,
    loading,
    error,
    connection,
    lastUpdatedAt,
    failureCount,
    refresh,
    watchNow,
  } = useMigrationLive(id);
  const platformRole = usePlatformAuthStore((s) => s.user?.role);
  const canManage = useMemo(
    () => canPlatform(platformRole, 'platform:system:manage'),
    [platformRole],
  );
  // Owned by the PAGE, not the panel: finalizing reloads the migration, which
  // unmounts the panel (its status is no longer VERIFYING). A result held in
  // the panel would vanish at the moment it was earned.
  const [finalizeResult, setFinalizeResult] = useState<ProviderMigrationFinalizeResultDto | null>(
    null,
  );
  // Same reasoning as finalizeResult: reloading the migration unmounts the
  // execute panel (its status is no longer DRY_RUN_COMPLETE/READY), so the
  // outcome is owned by the page or it would vanish when it was earned.
  const [runResult, setRunResult] = useState<ProviderMigrationRunResultDto | null>(null);
  // Same reasoning again: a successful ownership flip re-derives the workflow to
  // FINALIZE_READY, which unmounts the ownership panel. The page keeps the
  // outcome so the operator can still read what moved.
  const [flipResult, setFlipResult] = useState<ProviderMigrationOwnershipResultDto | null>(null);

  if (loading) {
    return (
      <div className="p-6" data-testid="migration-loading">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4" />
          Loading migration…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="migration-error">
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-destructive">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" />
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!migration) {
    return (
      <div className="p-6" data-testid="migration-empty">
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">This migration could not be found.</p>
        </div>
      </div>
    );
  }

  const snapshot = migration.dryRunReport ?? {};
  const sourceName = snapshot.source?.driverId ?? migration.sourceProviderId;
  const targetName = snapshot.destination?.driverId ?? migration.targetProviderId;
  // The single source of truth for what this page offers. Derived from server
  // state on every render, so it cannot go stale relative to what is displayed.
  const workflow = deriveMigrationWorkflow(migration, canManage);
  const total = migration.totalObjects ?? migration.itemCount;
  const excludedCount = snapshot.exclusions?.excludedCount ?? 0;
  const excludedByStatus = snapshot.exclusions?.excludedByStatus ?? {};
  const bytes = snapshot.plannedBytes ?? null;
  const isPreExecution = PRE_EXECUTION_STATUSES.has(migration.status);

  return (
    <div className="space-y-6 p-6" data-testid="provider-migration-detail">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div>
        <Link
          to={PLATFORM_ROUTES.STORAGE_PROVIDER_MIGRATIONS}
          className="mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
          Provider Migrations
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="flex items-center gap-2.5 text-2xl font-semibold text-foreground">
            <span className="uppercase">{sourceName}</span>
            <FontAwesomeIcon icon={faArrowRightLong} className="h-4 w-4 text-muted-foreground" />
            <span className="uppercase">{targetName}</span>
          </h1>
          <StatusBadge status={migration.status} />
        </div>

        <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
          <div className="flex gap-2">
            <dt>Migration ID</dt>
            <dd className="font-mono text-xs text-foreground" data-testid="migration-id">
              {migration.id}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt>{isPreExecution ? 'Planned' : 'Completed'}</dt>
            <dd className="text-foreground">
              {fmtDateTime(isPreExecution ? migration.createdAt : migration.completedAt)}
            </dd>
          </div>
        </dl>
      </div>

      {/* ── Where the migration is, and what comes next ──────────────────── */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="workflow-panel">
        <WorkflowStepper
          current={workflow.stepIndex}
          stage={workflow.stage}
          complete={workflow.stage === 'COMPLETED'}
        />
        {workflow.nextActionLabel !== null && (
          <p className="mt-3 text-sm text-foreground" data-testid="workflow-next-action">
            <span className="font-semibold">Next: </span>
            {workflow.nextActionLabel}
          </p>
        )}
      </div>

      {/*
        ── Run outcome ───────────────────────────────────────────────────────
        Kept by the page, not the panel: reloading re-derives the workflow and
        unmounts the panel that produced this.
      */}
      {runResult !== null && (
        <div
          className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5"
          data-testid="execute-success"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
            <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
            Migration approved and run successfully — status is now {runResult.status}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Objects have been copied and verified.{' '}
            <strong className="text-foreground">Ownership has not changed yet.</strong>
          </p>
          {/*
            Uploaded and REUSED are reported separately and on purpose. A
            destination object that already exists with an identical SHA-256 is a
            successful, reusable object — not a failure and not a skipped item —
            and an operator who cannot see that will read a low "copied" count as
            a stalled migration.
          */}
          <dl
            className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
            data-testid="run-outcome-stats"
          >
            {[
              { label: 'Total', value: runResult.totalObjects },
              { label: 'Uploaded', value: runResult.copied },
              { label: 'Reused', value: runResult.alreadyPresentAndIdentical },
              { label: 'Verified', value: runResult.verified },
              { label: 'Failed', value: runResult.failed },
              { label: 'Bytes', value: runResult.bytesCopied.toLocaleString() },
            ].map((t) => (
              <div
                key={t.label}
                data-testid={`run-stat-${t.label.toLowerCase()}`}
                className="rounded-lg border border-border bg-card p-2.5"
              >
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{t.label}</dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
                  {typeof t.value === 'number' ? t.value.toLocaleString() : t.value}
                </dd>
              </div>
            ))}
          </dl>
          {runResult.alreadyPresentAndIdentical > 0 && (
            <p className="mt-2 text-sm text-muted-foreground" data-testid="run-reused-note">
              <strong className="text-foreground">
                {runResult.alreadyPresentAndIdentical} object
                {runResult.alreadyPresentAndIdentical === 1 ? ' was' : 's were'} already present on
                the destination with an identical checksum
              </strong>{' '}
              and were reused instead of being uploaded again. They are verified and count as
              successful — the migration continued normally. Nothing was deleted.
            </p>
          )}
        </div>
      )}

      {/*
        ── Execution ─────────────────────────────────────────────────────────
        Offered only at the PLANNED (approve then run) or APPROVED (run only,
        never re-approve) stages, and only for a role holding
        platform:system:manage — both folded into the workflow. The panel never
        calls a mutation without an explicit confirmation.
      */}
      {(workflow.canApproveAndRun || workflow.canRun) && runResult === null && (
        <ExecuteMigrationPanel
          migrationId={migration.id}
          status={migration.status}
          totalObjects={migration.totalObjects}
          snapshot={migration.dryRunReport}
          onRunStarted={watchNow}
          onExecuted={(r) => {
            setRunResult(r);
            void refresh();
          }}
        />
      )}

      {/* ── Ownership outcome ─────────────────────────────────────────────── */}
      {flipResult !== null && (
        <div
          className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5"
          data-testid="ownership-success"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
            <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
            Ownership moved to <span className="uppercase">{targetName}</span>
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {flipResult.flipped} of {flipResult.totalItems} objects moved ·{' '}
            {flipResult.alreadyInTargetState} were already there · {flipResult.failed} failed ·{' '}
            {flipResult.tenantsProcessed} tenant(s) · {flipResult.objectsDeleted} deleted · the
            migration status was not changed.
          </p>
          {flipResult.failed > 0 && (
            <p className="mt-1 text-sm text-amber-700" data-testid="ownership-success-failures">
              Not every object moved. Finalization will refuse until all of them have:{' '}
              {Object.entries(flipResult.failuresByReason)
                .map(([reason, count]) => `${reason} × ${count}`)
                .join(', ')}
            </p>
          )}
        </div>
      )}

      {/*
        ── Ownership transition ──────────────────────────────────────────────
        The step that was missing from this console. Offered whenever the copy
        is verified but the server has not confirmed that every asset is owned
        by the target — including when it reports no ownership state at all, in
        which case only the read-only rehearsal is useful. The flip itself is
        additionally gated on platform:system:manage inside the panel.
      */}
      {(workflow.canPreviewOwnership || workflow.canFlipOwnership) && (
        <OwnershipTransferPanel
          migrationId={migration.id}
          sourceName={sourceName}
          targetName={targetName}
          ownership={migration.ownership ?? null}
          canManage={workflow.canFlipOwnership}
          onFlipped={(r) => {
            setFlipResult(r);
            void refresh();
          }}
        />
      )}

      {/*
        ── Why finalization is not being offered ─────────────────────────────
        Shown INSTEAD of a Finalize button the server would refuse. This is the
        sentence the operator previously had to infer from a bare 409.
      */}
      {workflow.finalizeBlockedReason !== null && (
        <div
          className="rounded-xl border border-border bg-muted/40 p-4"
          data-testid="finalize-blocked"
        >
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <FontAwesomeIcon
              icon={faArrowRightArrowLeft}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
            />
            <span>
              <strong className="text-foreground">Finalization is not available yet. </strong>
              {workflow.finalizeBlockedReason}
            </span>
          </p>
        </div>
      )}

      {/* ── Finalization outcome ──────────────────────────────────────────── */}
      {finalizeResult !== null && (
        <div
          className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5"
          data-testid="finalize-success"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
            <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
            Migration finalized — status is now {finalizeResult.status}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {finalizeResult.gatesPassed} of {finalizeResult.gatesEvaluated} completion gates passed
            · {finalizeResult.targetObjectsRehashed} destination objects re-hashed ·{' '}
            {finalizeResult.sourceObjectsConfirmedPresent} source objects confirmed still present ·
            nothing was deleted.
          </p>
        </div>
      )}

      {/*
        ── Finalization ──────────────────────────────────────────────────────
        Rendered ONLY at stage FINALIZE_READY: status VERIFYING, every item
        verified, AND the server confirming every asset is already owned by the
        target provider. That last condition is the one that was missing, and is
        the reason this panel used to appear in a state the server refuses.
      */}
      {workflow.canFinalize && finalizeResult === null && (
        <FinalizeMigrationPanel
          migrationId={migration.id}
          verifiedObjects={migration.verifiedObjects}
          totalObjects={migration.totalObjects}
          sourceName={sourceName}
          targetName={targetName}
          onFinalized={(r) => {
            setFinalizeResult(r);
            void refresh();
          }}
        />
      )}

      {/* ── Summary ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Total Objects" value={total} />
        <StatTile
          label="Migrated"
          value={migration.migratedObjects}
          tone={isPreExecution ? 'muted' : 'good'}
          {...(isPreExecution ? { hint: 'Not started' } : {})}
        />
        <StatTile
          label="Verified"
          value={migration.verifiedObjects}
          tone={isPreExecution ? 'muted' : 'good'}
          {...(isPreExecution ? { hint: 'Not started' } : {})}
        />
        <StatTile
          label="Failed"
          value={migration.failedObjects}
          tone={migration.failedObjects > 0 ? 'warn' : 'muted'}
        />
        <StatTile label="Skipped" value={migration.skippedObjects} tone="muted" />
        <StatTile
          label="Protected / Excluded"
          value={excludedCount}
          tone="muted"
          hint="Intentionally not migrated"
        />
      </div>

      {/*
        ── Progress, or the dry-run panel for a plan that has not run ────────
        Once execution has begun this is the LIVE view: it re-reads the server
        while the copy runs, so the counts, the percentage and the current object
        advance on their own. Nothing in it is interpolated by the browser.
      */}
      {isPreExecution ? (
        <DryRunPanel total={total} status={migration.status} />
      ) : (
        <LiveProgressPanel
          status={migration.status}
          progress={migration.progress}
          fallback={{
            migrated: migration.migratedObjects,
            verified: migration.verifiedObjects,
            failed: migration.failedObjects,
            skipped: migration.skippedObjects,
            total,
          }}
          items={items}
          connection={connection}
          lastUpdatedAt={lastUpdatedAt}
          failureCount={failureCount}
          running={migration.status === 'RUNNING'}
        />
      )}

      {/* ── Protected assets — excluded, NOT failed ─────────────────────────── */}
      {excludedCount > 0 && (
        <div
          className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-5"
          data-testid="protected-assets-notice"
        >
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4 text-sky-600" />
            {excludedCount} protected asset{excludedCount === 1 ? '' : 's'} excluded
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            These assets were <strong className="text-foreground">deliberately excluded</strong>{' '}
            from the migration and remain on the source provider. They did{' '}
            <strong className="text-foreground">not</strong> fail — they were never candidates.
          </p>
          {Object.keys(excludedByStatus).length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">
              {Object.entries(excludedByStatus).map(([status, count]) => (
                <li
                  key={status}
                  className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground"
                >
                  {status.replace(/_/g, ' ')}: <span className="font-semibold">{count}</span>
                </li>
              ))}
            </ul>
          )}
          {snapshot.exclusions?.policy && (
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              {snapshot.exclusions.policy}
            </p>
          )}
        </div>
      )}

      {/* ── Details ────────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-1 text-sm font-semibold text-foreground">Details</h2>
        <dl className="divide-y divide-border">
          <DetailRow label="Source provider">
            <span className="uppercase">{sourceName}</span>
          </DetailRow>
          <DetailRow label="Target provider">
            <span className="uppercase">{targetName}</span>
          </DetailRow>
          <DetailRow label="Status">
            <StatusBadge status={migration.status} />
          </DetailRow>
          <DetailRow label="Total objects">{total.toLocaleString()}</DetailRow>
          <DetailRow label="Migrated / Verified">
            {migration.migratedObjects.toLocaleString()} /{' '}
            {migration.verifiedObjects.toLocaleString()}
          </DetailRow>
          <DetailRow label="Failed / Skipped">
            {migration.failedObjects.toLocaleString()} / {migration.skippedObjects.toLocaleString()}
          </DetailRow>
          <DetailRow label="Bytes migrated">
            {bytes !== null ? (
              <>
                {fmtBytes(bytes)}{' '}
                <span className="text-muted-foreground">{`(${fmtExactBytes(bytes)})`}</span>
              </>
            ) : (
              '—'
            )}
          </DetailRow>
          <DetailRow label="Created">{fmtDateTime(migration.createdAt)}</DetailRow>
          <DetailRow label="Started">{fmtDateTime(migration.startedAt)}</DetailRow>
          <DetailRow label="Completed">{fmtDateTime(migration.completedAt)}</DetailRow>
          <DetailRow label="Paused">{fmtDateTime(migration.pausedAt)}</DetailRow>
          <DetailRow label="Rolled back">
            {migration.rolledBackAt ? (
              fmtDateTime(migration.rolledBackAt)
            ) : (
              <span className="text-muted-foreground">Not rolled back</span>
            )}
          </DetailRow>
          <DetailRow label="Source cleanup">
            <span
              className="inline-flex items-center gap-1.5 font-medium text-foreground"
              data-testid="source-cleanup-state"
            >
              <FontAwesomeIcon icon={faLock} className="h-3 w-3 text-muted-foreground" />
              Not performed — source copies retained
            </span>
          </DetailRow>
          {snapshot.sourceRetentionRule && (
            <DetailRow label="Retention rule">
              <span className="text-muted-foreground">{snapshot.sourceRetentionRule}</span>
            </DetailRow>
          )}
        </dl>
      </div>

      {/* ── Items ──────────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <FontAwesomeIcon icon={faDatabase} className="h-4 w-4 text-muted-foreground" />
            Migrated objects
          </h2>
          <span className="text-sm text-muted-foreground" data-testid="items-count">
            {items.length.toLocaleString()}
          </span>
        </div>

        {items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="items-empty">
            No migration items to display.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Asset</th>
                  <th className="px-4 py-2.5 font-medium">Class</th>
                  <th className="px-4 py-2.5 font-medium">From → To</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Verified</th>
                  <th className="px-4 py-2.5 font-medium">Checksum</th>
                  <th className="px-4 py-2.5 text-right font-medium">Attempts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/40">
                    <td className="max-w-xs px-4 py-2.5">
                      <span className="block truncate font-medium text-foreground">
                        {item.fileName ?? item.storageKey.split('/').pop() ?? item.storageKey}
                      </span>
                      <span className="block truncate font-mono text-xs text-muted-foreground">
                        {item.storageKey}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{item.assetClass ?? '—'}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 uppercase text-muted-foreground">
                      {sourceName} → {targetName}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-2.5">
                      {item.verified ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-600">
                          <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not verified</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {item.checksumsMatch ? (
                        <span
                          className="font-mono text-xs text-emerald-600"
                          title="Source and target SHA-256 match"
                        >
                          match {shortHash(item.targetChecksum)}
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                      {item.attemptCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Each step of a provider migration is a separate, explicitly confirmed action, and each is
        re-verified by the server before it is allowed. Source objects are always retained: no step
        offered here deletes anything.
      </p>
    </div>
  );
}
